// import { createStore, applyMiddleware, compose } from "redux";
// import thunk from "redux-thunk";
import { createLogger } from "redux-logger";
import { responsiveStoreEnhancer, CALCULATE_RESPONSIVE_STATE } from "redux-responsive";
import { propEq } from "ramda";
// import { composeWithDevTools } from "redux-devtools-extension";
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistStore } from "redux-persist";

import { configureStore } from "@reduxjs/toolkit";
import { querySyncEnhancer } from "~/store/enhancers/query-sync";

import { remoteMaterialDB, remoteUserDB } from "~/libs/pouch";
import PouchMiddleware from "~/store/middlewares/pouch-redux-middleware";
import rehydrationMiddleware from "~/store/middlewares/rehydration";
import duckReducer from "~/store/ducks";

import {
  addMaterial,
  addAllMaterials,
  removeMaterial,
  updateMaterial
} from "~/store/ducks/application/materials/materials";

import { addBookmark, addAllBookmarks, removeBookmark, updateBookmark } from "~/store/ducks/user/bookmarks";

import {
  addPresentationMarker,
  addAllPresentationMarkers,
  updatePresentationMarker
} from "~/store/ducks/user/presentationMarkers";

import { addNote, addAllNotes, updateNote } from "~/store/ducks/user/notes";

import { addAnnotation, addAllAnnotations, removeAnnotation, updateAnnotation } from "~/store/ducks/user/annotations";

import { addAllDlsUserMaterials, updateDlsUserMaterial } from "~/store/ducks/user/dlsUserMaterials";

import { addDlsUserMaterial } from "~/store/actions/dls";

import { hasUserAdminGroup } from "~/utils";
import { SHOW_MODAL } from "./ducks/domain/ui/modal";

const pouchMiddleware = PouchMiddleware([
  {
    useLiveSync: hasUserAdminGroup(),
    path: "/application/materials/materials",
    db: remoteMaterialDB,
    actions: {
      remove: doc => removeMaterial(doc),
      insert: doc => addMaterial(doc),
      batchInsert: docs => addAllMaterials(docs),
      update: doc => updateMaterial(doc)
    },
    changeFilter: propEq("type", "material")
  },
  {
    useLiveSync: true,
    path: "/user/bookmarks",
    db: remoteUserDB,
    actions: {
      remove: doc => removeBookmark(doc),
      insert: doc => addBookmark(doc),
      batchInsert: docs => addAllBookmarks(docs),
      update: doc => updateBookmark(doc)
    },
    changeFilter: propEq("type", "bookmark")
  },
  {
    useLiveSync: true,
    path: "/user/presentationMarkers",
    db: remoteUserDB,
    actions: {
      remove: () => {
        console.error("invalid remove");
      },
      insert: doc => addPresentationMarker(doc),
      batchInsert: docs => addAllPresentationMarkers(docs),
      update: doc => updatePresentationMarker(doc)
    },
    changeFilter: propEq("type", "presentationMarker")
  },
  {
    useLiveSync: true,
    path: "/user/notes",
    db: remoteUserDB,
    actions: {
      remove: () => {},
      insert: doc => addNote(doc),
      batchInsert: docs => addAllNotes(docs),
      update: doc => updateNote(doc)
    },
    changeFilter: propEq("type", "note")
  },
  {
    useLiveSync: true,
    path: "/user/dlsUserMaterials",
    db: remoteUserDB,
    actions: {
      remove: () => {},
      insert: doc => addDlsUserMaterial(doc),
      batchInsert: docs => addAllDlsUserMaterials(docs),
      update: doc => updateDlsUserMaterial(doc)
    },
    changeFilter: propEq("type", "dlsUserMaterial")
  },
  {
    useLiveSync: true,
    path: "/user/annotations/annotations",
    db: remoteUserDB,
    actions: {
      remove: doc => removeAnnotation(doc),
      insert: doc => addAnnotation(doc),
      batchInsert: docs => addAllAnnotations(docs),
      update: doc => updateAnnotation(doc)
    },
    changeFilter: propEq("type", "annotation")
  }
]);

const configure = initialState => {
  const store = configureStore({
    reducer: duckReducer,
    preloadedState: initialState,
    devTools: process.env.NODE_ENV === "development",
    middleware: getDefaultMiddleware => {
      const middleware = getDefaultMiddleware({
        thunk: true,
        serializableCheck: {
          ignoredPaths: ["domain.ui.modal", "domain.config.usersettings"],
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, CALCULATE_RESPONSIVE_STATE, SHOW_MODAL],
          warnAfter: 128
        },
        immutableCheck: {
          ignoredPaths: ["domain.ui.modal", "application.materials.materials"],
          warnAfter: 256
        }
      }).concat(pouchMiddleware, rehydrationMiddleware);

      if (process.env.NODE_ENV === "development") {
        const logger = createLogger({ collapsed: true });
        middleware.push(logger);
      }

      return middleware;
    },
    enhancers: defaultEnhancers => [querySyncEnhancer, responsiveStoreEnhancer, ...defaultEnhancers]
  });

  const persistor = persistStore(store);

  return { store, persistor };
};

export default configure;
